import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Check, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Reveal, EstimateBanner } from '../components/shared';
import { PageHeader } from './Flooring';
import { categories, categorySamples, categoryTypes } from '../mock/mock';

export default function CategoryDetail() {
  const { slug } = useParams();
  const cat = categories.find((c) => c.slug === slug);
  if (!cat) return <Navigate to="/flooring" replace />;
  const others = categories.filter((c) => c.slug !== slug).slice(0, 3);
  const samples = categorySamples[slug] || [];
  const types = categoryTypes[slug] || [];

  return (
    <>
      <PageHeader eyebrow="Flooring" title={cat.name} sub={cat.short} image={cat.hero} />

      <section className="max-w-7xl mx-auto px-6 py-20 md:py-24 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <img src={cat.image} alt={cat.name} className="rounded-2xl w-full h-[460px] object-cover shadow-lg" />
        </Reveal>
        <Reveal delay={120}>
          <h2 className="font-display text-3xl md:text-[38px] font-semibold text-brand-charcoal leading-tight text-balance">Why choose {cat.name.toLowerCase()}?</h2>
          <p className="mt-5 text-brand-stone text-[17px] leading-relaxed">{cat.intro}</p>
          <ul className="mt-7 grid sm:grid-cols-2 gap-3">
            {cat.features.map((f) => (
              <li key={f} className="flex items-start gap-2.5 text-brand-charcoal">
                <span className="mt-0.5 w-5 h-5 rounded-full bg-brand-clay/12 text-brand-clay flex items-center justify-center shrink-0"><Check size={13} /></span>
                <span className="text-[15px]">{f}</span>
              </li>
            ))}
          </ul>
          <Button asChild className="mt-8 bg-brand-clay hover:bg-brand-clayDark text-white rounded-full px-7 h-13 py-3.5 font-medium">
            <Link to="/estimate">Get Free {cat.name.split(' ')[0]} Samples <ArrowRight size={17} className="ml-1" /></Link>
          </Button>
        </Reveal>
      </section>

      {/* types available in the US */}
      <section className="bg-brand-cream">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto">
              <span className="text-brand-clay text-xs font-semibold uppercase tracking-[0.25em]">Every Option, Covered</span>
              <h2 className="font-display text-3xl md:text-[38px] font-semibold text-brand-charcoal mt-3 leading-tight">{cat.name.split(' ')[0]} types available in the U.S.</h2>
              <p className="mt-4 text-brand-stone text-[17px] leading-relaxed">From construction to fiber to format — here’s the full range we can source and install for you.</p>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              {types.map((t) => (
                <span key={t} className="px-5 py-2.5 bg-white rounded-full border border-brand-line text-brand-charcoal text-sm font-medium shadow-sm hover:border-brand-clay hover:text-brand-clay transition-colors">{t}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* sample gallery */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-24">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto">
              <span className="text-brand-clay text-xs font-semibold uppercase tracking-[0.25em]">Sample Gallery</span>
              <h2 className="font-display text-3xl md:text-[42px] font-semibold text-brand-charcoal mt-3 leading-tight">Explore {cat.name.toLowerCase()} styles</h2>
              <p className="mt-4 text-brand-stone text-[17px] leading-relaxed">A taste of what we can bring to your door. Like something? We’ll drop off these samples so you can see them in your own home.</p>
            </div>
          </Reveal>
          <div className="mt-14 grid gap-6 grid-cols-2 lg:grid-cols-3">
            {samples.map((s, idx) => (
              <Reveal key={s.name} delay={(idx % 3) * 80}>
                <div className="group rounded-2xl overflow-hidden bg-white border border-brand-line shadow-sm hover:shadow-xl transition-shadow">
                  <div className="overflow-hidden aspect-[4/3]">
                    <img src={s.image} alt={s.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-5">
                    <p className="text-brand-clay text-[11px] font-semibold uppercase tracking-wider">{s.brand}</p>
                    <h3 className="font-display text-lg font-semibold text-brand-charcoal mt-0.5">{s.name}</h3>
                    <p className="text-brand-stone text-sm mt-1">{s.finish}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Button asChild className="bg-brand-clay hover:bg-brand-clayDark text-white rounded-full px-8 h-13 py-3.5 font-medium">
              <Link to="/estimate">Request These Samples <ArrowRight size={17} className="ml-1" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* brands */}
      <section className="bg-brand-cream">
        <div className="max-w-7xl mx-auto px-6 py-16 text-center">
          <span className="text-brand-clay text-xs font-semibold uppercase tracking-[0.25em]">Trusted brands</span>
          <h3 className="font-display text-2xl md:text-3xl font-semibold text-brand-charcoal mt-3">Names you know and trust</h3>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {cat.brands.map((b) => (
              <span key={b} className="px-6 py-3 bg-white rounded-full border border-brand-line text-brand-charcoal font-medium shadow-sm">{b}</span>
            ))}
          </div>
        </div>
      </section>

      <EstimateBanner />

      {/* other categories */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="flex items-center justify-between mb-10">
          <h3 className="font-display text-2xl md:text-3xl font-semibold text-brand-charcoal">Explore other flooring</h3>
          <Link to="/flooring" className="inline-flex items-center gap-1.5 text-brand-clay font-semibold text-sm hover:gap-2.5 transition-all"><ArrowLeft size={16} /> All Flooring</Link>
        </div>
        <div className="grid gap-6 sm:grid-cols-3">
          {others.map((c, idx) => (
            <Reveal key={c.slug} delay={idx * 80}>
              <Link to={`/flooring/${c.slug}`} className="cat-card group block relative rounded-2xl overflow-hidden h-64 shadow-sm hover:shadow-xl transition-shadow">
                <img src={c.image} alt={c.name} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-charcoal/85 to-transparent" />
                <h4 className="absolute bottom-5 left-5 font-display text-xl font-semibold text-brand-ivory">{c.name}</h4>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
}
